<template>
  <div
    :class="['filter-manager', 'smooth-shadow', { hide: !isFiltersVisible }]"
  >
    <div class="filter-manager-header">
      <Spinner v-if="isCategoricalRequestInProgress" small />
      <Icon v-else size="18">filter_list</Icon>
      <h3 class="title">Filters</h3>
      <expand />
      <Icon type="button" size="18" @click="toggleFilterManager">clear</Icon>
    </div>
    <div v-if="showEdit" class="tabs">
      <ul>
        <li
          v-for="tab in tabs"
          :key="tab"
          :class="[{ active: activeTab === tab }]"
          @click="activeTab = tab"
        >
          {{ tab }}
        </li>
      </ul>
    </div>
    <CategoricalList
      v-if="showEdit"
      :activeTab="activeTab"
      :model="getActiveEntity.entityModel"
      :options="getEntityFilters"
      @toggleFilter="toggleFilterItem"
      @toggle="toggleEntityFilter"
      @change="updateCategories"
      @close="toggleEditMode"
    />
    <div v-if="!showEdit" class="layout-contents">
      <simplebar class="layout-contents" data-auto-hide="true">
        <div class="contents">
          <!-- Add filter button -->
          <div
            v-if="!showEdit && getConfig.isTempWidgets"
            class="add-filter-button"
            @click="showEdit = !showEdit"
          >
            <div class="add-icon">
              <icon>add</icon>
            </div>
            <div class="flex-col flex-expand mar-left-10">
              <h4 class="title">Add filters</h4>
              <p class="description ellipsis">
                Browse filters to analyze the data
              </p>
            </div>
            <icon type="box" size="18">keyboard_arrow_right</icon>
          </div>
          <component
            v-for="filter in getCategoricalWidgets"
            :key="filter.name"
            :is="filter.component"
            :model="getActiveEntity.entityModel"
            :data="filter"
            :isEditable="getConfig.isTempWidgets"
            @toggleFilter="toggleFilterItem"
            @toggleLock="toggleCategoryLock(filter)"
          />
        </div>
      </simplebar>
    </div>
    <!-- <div
      v-if="!showEdit && getConfig.isTempWidgets"
      class="filter-manager-footer"
    >
      <expand />
      <button class="prop-action-button save" @click="showEdit = !showEdit">
        <Icon size="18">add_box</Icon>
        <strong>Add filters</strong>
      </button>
    </div> -->

    <div v-if="showEdit" class="filter-manager-footer">
      <button class="prop-action-button" @click="updateCategories">
        <Icon size="18">arrow_back</Icon>back to filters
      </button>
      <expand />
    </div>
  </div>
</template>

<script>
import CategoricalFilter from "@/components/metrics/views/CategoricalFilter";
import MeasureFilter from "@/components/metrics/views/MeasureFilter";
import CategoricalList from "@/components/metrics/views/CategoricalList";
import { mapActions, mapGetters } from "vuex";
import { ActiveCategory } from "../../../models/Categorical.model";
import { startTour } from "@/components/metrics/tours/FilterManager.tour";

export default {
  components: {
    CategoricalFilter,
    CategoricalList,
    MeasureFilter,
  },
  data() {
    return {
      showEdit: false,
      activeTab: "Dimensions",
      tabs: ["Dimensions", "Measures"],
    };
  },
  computed: {
    ...mapGetters("Datasources", ["getDataSources", "getDataSourceDetails"]),
    ...mapGetters("Entities", ["getEntities"]),
    ...mapGetters("LayerManager", ["getActiveEntity"]),
    ...mapGetters("DataManager", [
      "isRequestInProgress",
      "isCategoricalRequestInProgress",
    ]),
    ...mapGetters("FilterManager", [
      "getSelectedFilters",
      "getAllFilters",
      "getAppliedFilters",
      "getLockedFilters",
    ]),
    ...mapGetters("StudioManager", ["getConfig", "isFiltersVisible"]),
    /**
     * Returns all the selected filters for the entity model
     */
    selectedFilters() {
      if (!this.getActiveEntity) return null;
      const { layer, entityModel } = this.getActiveEntity;
      const appliedFilters = this.getAppliedFilters;

      // get all the selected filters of the entity model
      const selectedFilters = this.getSelectedFilters.filter(
        item => item.entity_model_id === entityModel.id
      );

      return selectedFilters;
    },
    getCategoricalWidgets() {
      if (
        !(
          this.getAllFilters &&
          this.getAllFilters.length > 0 &&
          this.getActiveEntity
        )
      )
        return null;
      const { layer, entityModel } = this.getActiveEntity;
      if (!(layer && entityModel)) return;

      /**
       * Check if the locked filters are present for this entity model
       */
      let lockedEntityFilters = [];
      if (this.getLockedFilters && this.getLockedFilters.length > 0) {
        lockedEntityFilters = this.getLockedFilters
          .filter(item => item.entity_model_id === entityModel.id)
          .map(item => item.id);
      }
      /**
       * Get only those filters from the filter Manger associated with the entity model id
       */
      return this.getAllFilters
        .filter(item => item.meta.id === entityModel.id)
        .map(item => {
          // check if the filter item is applied or not
          const dataItems = item.data.map(data => {
            const filterIitem = new ActiveCategory({
              category_name: item.name,
              category_id: item.id,
              item_id: data.id,
              entity_model_id: entityModel.id,
            });
            const isPresentItem = this.getAppliedFilters.find(item =>
              filterIitem.compare(item)
            );
            let isActive = isPresentItem ? true : false;

            return {
              ...data,
              isActive,
            };
          });

          let chits = [];
          if (item.is_measure) {
            // get all the applied filters of measures
            chits = this.getAppliedFilters
              .filter(
                filter =>
                  filter.category_id === item.id &&
                  item.meta.id === entityModel.id
              )
              .map(filter => {
                return `${item.name} ${filter.comparison} ${filter.item_id}`;
              });
          }

          return {
            ...item,
            data: dataItems,
            chits,
            isLocked: lockedEntityFilters.includes(item.id),
          };
        });
    },
    getEntityFilters() {
      // check if dataSource and Entity Data is present
      if (!this.getDataSources || !this.getEntities) return null;
      // check if active layers are present
      if (!this.getActiveEntity) return null;
      const { layer, entityModel } = this.getActiveEntity;
      const columns = [];
      // get the details of the selected entityModel
      const selectedEntity = this.getEntities.find(
        item => item.id === entityModel.entity_id
      );

      let selectedColIds = this.selectedFilters.map(item => item.id);

      if (selectedEntity) {
        // get all the datasource details of the selected entity
        selectedEntity.datasources.map(datasource => {
          // get all the selected columns of the entity from each of its datasource
          this.getDataSourceDetails(datasource.datasource_id).columns.forEach(
            column => {
              // add the columns only if it's a measure
              if (
                datasource.selected_cols.includes(column.name) &&
                (column.is_dimension ||
                  (selectedEntity.entity_type === "trip" && column.is_measure))
              ) {
                columns.push({
                  ...column,
                  datasource_name: datasource.name,
                  isActive: selectedColIds.includes(column.id),
                  entity_model_id: entityModel.id,
                });
              }
            }
          );
        });
        return columns;
      } else {
        return null;
      }
    },
  },
  methods: {
    ...mapActions("Datasources", ["fetchDataSources"]),
    ...mapActions("Entities", ["fetchEntities"]),
    ...mapActions("FilterManager", [
      "toggleEntityFilter",
      "toggleFilter",
      "toggleFilterLock",
    ]),
    ...mapActions("StudioManager", ["toggleFilterManager"]),
    toggleEditMode() {
      this.showEdit = false;
    },
    updateCategories() {
      this.toggleEditMode();
      this.$emit("change");
    },
    toggleFilterItem(item) {
      if (item) {
        this.toggleFilter(item).then(() => {
          this.$emit("toggle");
        });
      } else {
        this.$emit("toggle");
      }
    },
    toggleCategoryLock(item) {
      const { layer, entityModel } = this.getActiveEntity;
      this.toggleFilterLock({
        id: item.id,
        entity_model_id: entityModel.id,
      });
    },
  },
  mounted() {
    if (this.getConfig && !this.getConfig.isPublicView) {
      if (!(this.getDataSources && this.getDataSources.length > 0)) {
        this.fetchDataSources();
      }

      if (!(this.getEntities && this.getEntities.length > 0)) {
        this.fetchEntities();
      }
    }
    if (this.$route.fullPath === "/studio/edit") {
      startTour();
    }
  },
};
</script>

<style lang="scss" scoped>
.filter-manager {
  height: 600px;
  width: 330px;
  max-height: 60%;
  background: var(--color-default-background);
  border: 1px solid var(--border-color);
  border-radius: 0 0 0 5px;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 99;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: 0.3s all ease-in-out;

  @include respond-below(lg) {
    height: 400px;
  }

  &.hide {
    transform: translateX(380px);
  }

  .filter-manager-header {
    background: var(--color-secondary);
    padding: 7px 5px 7px 15px;
    z-index: 10;
    border-radius: 0;
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--border-color);

    .title {
      margin-left: 10px;
    }
  }

  .tabs {
    display: flex;
    flex-direction: row;
    background: var(--color-secondary);

    ul {
      list-style-type: none;
      display: flex;
      flex: 1;
      margin: 0;
      padding: 0;
      border-bottom: 1px solid var(--border-color);
      align-items: center;

      li {
        padding: 10px 15px;
        border-radius: 3px 3px 0 0;
        border-bottom: 3px solid transparent;

        &:hover {
          background: var(--color-primary-light);
          cursor: pointer;
        }

        &.active {
          border-bottom: 3px solid var(--color-primary);
        }
      }
    }
  }

  .layout-contents {
    min-width: 0;
    height: 100%;
    flex: 1;
    overflow-y: auto;
    position: relative;
  }

  .add-filter-button {
    display: flex;
    flex-direction: row;
    align-items: center;
    background: var(--color-secondary);
    padding: 5px 10px 5px 5px;
    margin: 0 5px 5px 5px;
    border-radius: 5px;
    transition: 0.3s all ease-in-out;

    &:hover {
      cursor: pointer;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    }

    .add-icon {
      background: var(--color-secondary-light);
      border-radius: 5px;
      color: var(--color-primary);
      width: 45px;
      height: 45px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .title {
      font-weight: bold;
      color: var(--color-primary);
    }

    .description {
      margin: 0;
      font-size: 0.8rem;
      opacity: 0.7;
    }
  }

  .filter-manager-footer {
    display: flex;
    flex-direction: row;
    padding: 10px;
    background: var(--color-secondary);
    border-radius: 0 0 5px 5px;
    border-top: 1px solid var(--border-color);

    .prop-action-button {
      display: flex;
      align-items: center;
      padding: 10px;
      border: 0;
      border-radius: 3px;
      background: transparent;
      color: var(--font-color);
      text-transform: capitalize;
      transition: 0.3s all ease-in-out;

      &.save {
        background-color: var(--color-primary);
        color: var(--color-secondary);
        margin-left: 10px;

        &:hover {
          opacity: 0.8;
          color: var(--color-secondary);
        }
      }

      i {
        margin-right: 5px;
        pointer-events: none;
      }

      &:hover {
        cursor: pointer;
        color: var(--color-primary);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  .contents {
    flex: 1;
    padding: 5px 0;
    overflow: auto;
    height: 100%;
  }
}
</style>
